import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, CalendarClock, CheckCircle2, Clock, Info, Pencil, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardFooter, CardHeader } from '@/components/ui/Card'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { Skeleton } from '@/components/ui/Skeleton'
import { Textarea } from '@/components/ui/Textarea'
import { formatDate } from '@/utils/format'
import { familyReenrollmentQuery, useAnswerReenrollment } from '../api'
import type { FamilyReenrollment, ReenrollmentOption } from '../types'

function optionDescription(
  t: (k: string, o?: Record<string, unknown>) => string,
  o: ReenrollmentOption,
): string {
  if (o.kind === 'repeat') return t('Repeat the year in the same class')
  return o.series ? t('Next level, série {{series}}', { series: o.series }) : t('Next level')
}

function Steps({ step }: { step: number }) {
  const { t } = useTranslation('promotion')
  const labels = [t('Next year'), t('Details'), t('Review')]
  return (
    <ol className="flex items-center gap-2 text-xs" aria-label={t('Steps')}>
      {labels.map((label, i) => (
        <li
          key={label}
          aria-current={i === step ? 'step' : undefined}
          className={i === step ? 'font-semibold text-brand-700' : i < step ? 'text-ink-soft' : 'text-muted'}
        >
          {i + 1}. {label}
          {i < labels.length - 1 ? <span className="ml-2 text-subtle">›</span> : null}
        </li>
      ))}
    </ol>
  )
}

function Status({ data, onEdit }: { data: FamilyReenrollment; onEdit: () => void }) {
  const { t } = useTranslation('promotion')
  const r = data.request
  const p = data.placement
  if (p) {
    const where = p.targetSection?.label ?? p.targetClass?.name
    return (
      <Alert tone="success" title={t('Confirmed by the school')}>
        {p.decision === 'promote' || p.decision === 'repeat'
          ? t('Confirmed: {{place}}', { place: where ?? '—' })
          : p.decision === 'graduate'
            ? t('The school has recorded the end of studies.')
            : t('The school has recorded that the student leaves.')}
      </Alert>
    )
  }
  if (!r) return null
  return (
    <div className="space-y-3">
      <Alert tone="info" title={t('Request sent on {{date}}', { date: formatDate(r.requestedAt) })}>
        <p>
          {r.status === 'requested'
            ? t('Continues next year — wished class: {{class}}', { class: r.requestedClass?.name ?? '—' })
            : t('Does not continue next year')}
        </p>
        <p>{t('Waiting for confirmation by the school.')}</p>
      </Alert>
      {data.canAnswer ? (
        <Button variant="secondary" size="sm" leftIcon={<Pencil />} onClick={onEdit}>
          {t('Change my answer')}
        </Button>
      ) : null}
    </div>
  )
}

function Flow({ data, onDone }: { data: FamilyReenrollment; onDone: () => void }) {
  const { t } = useTranslation('promotion')
  const r = data.request
  const [step, setStep] = useState(0)
  const [returning, setReturning] = useState<'yes' | 'no' | ''>(
    r?.status === 'requested' ? 'yes' : r?.status === 'not_returning' ? 'no' : '',
  )
  const [classId, setClassId] = useState<string>(r?.requestedClass ? String(r.requestedClass.id) : '')
  const [note, setNote] = useState(r?.note ?? '')
  const answer = useAnswerReenrollment(data.studentId)
  const options = data.options ?? []
  const chosen = options.find((o) => String(o.classId) === classId)
  const hasSeries = options.filter((o) => o.kind === 'next' && o.series).length > 1

  const canNext = step === 0 ? returning !== '' : step === 1 ? returning === 'no' || classId !== '' : true
  const submit = () =>
    answer.mutate(
      {
        returning: returning === 'yes',
        requestedClassId: returning === 'yes' ? Number(classId) : null,
        note: note.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success(t('Your answer has been sent'))
          onDone()
        },
        onError: (e) => toast.error(t('Could not send your answer'), errorMessage(e)),
      },
    )

  return (
    <div className="space-y-4">
      <Steps step={step} />
      {step === 0 ? (
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-ink">
            {t('Will {{name}} continue at the school next year?', { name: data.firstName })}
          </legend>
          <RadioGroup
            appearance="cards"
            value={returning}
            onValueChange={(v) => setReturning(v as 'yes' | 'no')}
            options={[
              {
                value: 'yes',
                label: t('Yes, re-enrol'),
                description: t('Choose the class on the next step.'),
              },
              { value: 'no', label: t('No'), description: t('The school will not keep a place.') },
            ]}
          />
        </fieldset>
      ) : null}
      {step === 1 && returning === 'yes' ? (
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-ink">{t('Which class next year?')}</legend>
          {hasSeries ? (
            <p className="flex gap-2 text-xs text-muted">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              {t(
                'The next level is split by série (for example L: literature and languages, S: sciences, OSE: economics and social sciences). Choose the one you wish; the school confirms it with the year’s results.',
              )}
            </p>
          ) : null}
          <RadioGroup
            appearance="cards"
            value={classId}
            onValueChange={setClassId}
            options={options.map((o) => ({
              value: String(o.classId),
              label: o.name,
              description: optionDescription(t, o),
            }))}
          />
          <FormField label={t('A note for the school (optional)')}>
            <Textarea rows={2} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          </FormField>
        </fieldset>
      ) : null}
      {step === 1 && returning === 'no' ? (
        <FormField label={t('Reason (optional)')} hint={t('For example a move to another town.')}>
          <Textarea rows={3} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
        </FormField>
      ) : null}
      {step === 2 ? (
        <div className="space-y-2 rounded-control border border-line bg-surface-muted/60 p-4 text-sm">
          <p className="font-medium text-ink">
            {returning === 'yes'
              ? t('{{name}} continues in {{year}}', { name: data.firstName, year: data.toYear?.name ?? '' })
              : t('{{name}} does not continue in {{year}}', {
                  name: data.firstName,
                  year: data.toYear?.name ?? '',
                })}
          </p>
          {returning === 'yes' && chosen ? (
            <p>{t('Wished class: {{class}}', { class: chosen.name })}</p>
          ) : null}
          {note.trim() ? <p className="text-muted">“{note.trim()}”</p> : null}
          <p className="text-xs text-muted">
            {t('You can change your answer until the school confirms it.')}
          </p>
        </div>
      ) : null}
      <div className="flex justify-between gap-2">
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft />}
          disabled={step === 0}
          onClick={() => setStep(step - 1)}
        >
          {t('Back')}
        </Button>
        {step < 2 ? (
          <Button rightIcon={<ArrowRight />} disabled={!canNext} onClick={() => setStep(step + 1)}>
            {t('Next')}
          </Button>
        ) : (
          <Button leftIcon={<Send />} loading={answer.isPending} onClick={submit}>
            {t('Send my answer')}
          </Button>
        )}
      </div>
    </div>
  )
}

/** The re-enrolment card of one child: the step-by-step answer, then its status. */
export function ReenrollmentCard({
  studentId,
  name,
  onlyWhenOpen = false,
}: {
  studentId: number
  name: string
  /** Dashboard use: hidden unless families can answer now or already did. */
  onlyWhenOpen?: boolean
}) {
  const { t } = useTranslation('promotion')
  const { data, isPending } = useQuery(familyReenrollmentQuery(studentId))
  const [editing, setEditing] = useState(false)

  if (isPending && onlyWhenOpen) return null
  if (isPending)
    return (
      <Card>
        <CardBody>
          <Skeleton className="h-24 w-full" />
        </CardBody>
      </Card>
    )
  if (!data?.available) return null
  if (onlyWhenOpen && !data.isOpen && !data.request && !data.placement) return null

  const showFlow = data.canAnswer && (editing || !data.request)
  return (
    <Card>
      <CardHeader
        icon={<CalendarClock />}
        title={t('Re-enrolment {{year}}', { year: data.toYear?.name ?? '' })}
        description={name}
        actions={
          data.isOpen ? (
            <Badge tone="success" dot>
              {t('Open')}
            </Badge>
          ) : (
            <Badge tone="neutral">{t('Closed')}</Badge>
          )
        }
      />
      <CardBody className="space-y-4">
        {data.deadline && data.isOpen ? (
          <p className="flex items-center gap-1.5 text-sm text-ink-soft">
            <Clock className="size-4" aria-hidden="true" />
            {t('Answer before {{date}}', { date: formatDate(data.deadline) })}
          </p>
        ) : null}
        {showFlow ? (
          <Flow data={data} onDone={() => setEditing(false)} />
        ) : data.request || data.placement ? (
          <Status data={data} onEdit={() => setEditing(true)} />
        ) : (
          <p className="text-sm text-muted">{t('Re-enrolment is closed. Contact the school office.')}</p>
        )}
      </CardBody>
      {data.message ? (
        <CardFooter className="justify-start">
          <p className="flex gap-2 text-sm whitespace-pre-line text-ink-soft">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden="true" />
            {data.message}
          </p>
        </CardFooter>
      ) : null}
    </Card>
  )
}
